import { getTagTypeTitle } from "../../../inputs/DynamicTagsInputs/FilterInput/utils/getTagTypeTitle";
import { trackEventGA } from "../../../../trackers/GA/trackEventGA";
import mixpanel from "mixpanel-browser";
import { flattenFilterConfig } from "./flattenFilterConfig";
import { getFilterAttributeKey, getSiblingFilterIds } from "./filterAttributeGrouping";
import { toActiveFilterSource } from "./toActiveFilterSource";

// Convert array of filters to object format
export const convertToInput = (activeFilters) =>
  activeFilters.reduce(
    (acc, filter) => ({
      ...acc,
      [filter.filterId]: filter.values,
    }),
    {}
  );

// Convert object format back to array format with target/source info
const convertToOutput = ({ updated, filterConfig, previousActiveFilters = [], showFilterModuleV3 = true }) => {
  const flatConfig = flattenFilterConfig(filterConfig);

  return Object.entries(updated).map(([filterId, values]) => {
    const config = flatConfig.find((f) => f.filterId === filterId);
    const prevFilter = previousActiveFilters.find((f) => f.filterId === filterId);

    // orGroupKey: the attribute identity the backend groups on for multi-path OR.
    // Entry points reaching the same attribute share this key → their queries OR;
    // everything else keeps a distinct key → AND. Omitted for non-groupable
    // filters (ranges etc.) so they stay solo — and omitted altogether on the
    // legacy filter UI, so its filters AND as they always have.
    const orGroupKey = showFilterModuleV3 ? getFilterAttributeKey(config) : null;

    // Preserve essential properties needed for filter matching and display
    // filterKey is critical for visualization segment clicks to match filters correctly
    return {
      filterId,
      values,
      target: config.target,
      // Slimmed: the option catalogue stays on the config (see toActiveFilterSource)
      source: toActiveFilterSource(config.source),
      filterKey: config.filterKey, // Required for handleVizSegmentClick to match filters
      display: config.display, // Preserve display name if available
      ...(orGroupKey && { orGroupKey }),
      ...(prevFilter?.intraFilterOperator && { intraFilterOperator: prevFilter.intraFilterOperator }),
    };
  });
};

const getGACategory = ({ filterConfig, filterId }) => {
  const config = flattenFilterConfig(filterConfig).find(
    (d) => d.filterId === filterId
  );
  if (!config) return "";

  // First try to use explicit display name
  if (config.display) return config.display;

  // Handle profile types
  if (config.source?.profileTypes) {
    return config.source.profileTypes.length > 1
      ? "Users"
      : getTagTypeTitle({ tagType: config.source.profileTypes[0] });
  }

  // Fallback to tag type
  return getTagTypeTitle({ tagType: config.source?.tagType });
};


const getDisplayNameOfThisFilter = ({filterConfig, thisFilterId}) => {
  const config = flattenFilterConfig(filterConfig).find(
    (d) => d.filterId === thisFilterId
  );
   //fallback to thisFilterId

  // First try to use explicit display name
  if (config?.display) return config.display;

  //else
  return thisFilterId //fallback
}

// Check if this is a number range filter type (passes object {min, max} instead of array)
const isNumberRangeFilterType = (filterConfig, filterId) => {
  const flatConfig = flattenFilterConfig(filterConfig);
  const config = flatConfig.find((f) => f.filterId === filterId);
  return config?.source?.filterType === 'numberRangeType';
};

// Same set of selected options, order-independent (values are {value, display}).
const sameValueSet = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const bValues = new Set(b.map((v) => v.value));
  return a.every((v) => bValues.has(v.value));
};

// Handle filter changes
export const handleOnChange = ({
  id,
  value,
  filterConfig,
  activeFilters,
  setActiveFilters,
  usedIn,
  // Where the change came from. `fromPicker: true` means the user is editing
  // inside the Add Filter cascade's options panel (see FilterListItem), which
  // reads as one continuous multi-select on a single field — so multi-path
  // fan-out stays live for the whole panel session. Applied-filter blocks omit
  // it, so editing a block is always an independent, per-direction edit.
  meta,
  // Multi-path OR (sibling fan-out + orGroupKey) belongs to the new (V3)
  // filter module. The legacy UI keeps its one-filter-one-query,
  // AND-everything behaviour.
  showFilterModuleV3 = true,
}) => {
  const currentFilters = convertToInput(activeFilters);
  const toOutput = (updated) =>
    convertToOutput({ updated, filterConfig, previousActiveFilters: activeFilters, showFilterModuleV3 });

  // Handle numberRangeType separately - it passes object {min, max} or {exact} instead of array
  if (isNumberRangeFilterType(filterConfig, id)) {
    // Check if value is empty (object without min/max/exact or empty object)
    const isEmpty = !value || (typeof value === 'object' && !Array.isArray(value) &&
      (Object.keys(value).length === 0 ||
        (value.min === undefined && value.max === undefined && value.exact === undefined)));

    if (isEmpty) {
      // Handle removal
      const { [id]: removed, ...remaining } = currentFilters;
      setActiveFilters(toOutput(remaining));
      return;
    }

    // Track with mixpanel for number range
    if (usedIn) {
      const selectedDisplay = value.exact !== undefined
        ? `${value.exact}`
        : `${value.min ?? ''} - ${value.max ?? ''}`;
      mixpanel.track('Filter Selected', {
        Selected: selectedDisplay,
        Feature: usedIn,
        'Filter Name': getDisplayNameOfThisFilter({filterConfig, thisFilterId: id})
      });
    }

    // Handle addition/update for number range
    const updated = { ...currentFilters, [id]: value };
    setActiveFilters(toOutput(updated));
    return;
  }

  // Standard handling below for array-based filters (multiselect, dateRange, etc.)
  const prevValues = currentFilters[id] || [];

  // Find additions and subtractions
  const additions = value.filter(newVal =>
    !prevValues.some(prevVal => prevVal.value === newVal.value)
  );

  const subtractions = prevValues.filter(prevVal =>
    !value.some(newVal => newVal.value === prevVal.value)
  );

  // Track with mixpanel
  if (usedIn && (additions.length > 0)) {
    mixpanel.track('Filter Selected', {
      Selected: additions.map(d => d.display).join(', '),
      Feature: usedIn,
      'Filter Name': getDisplayNameOfThisFilter({filterConfig, thisFilterId: id})
    });
  }

  if (usedIn && (subtractions.length > 0)) {
    mixpanel.track('Filter Deselected', {
      Deselected: subtractions.map(d => d.display).join(', '),
      Feature: usedIn,
      'Filter Name': getDisplayNameOfThisFilter({filterConfig, thisFilterId: id})
    });
  }

  // Multi-path fan-out: an attribute (a tag like "state", or a value-path field
  // like "gender") can be reached via several entry points — a direct field,
  // rollups, other datasets — each a separate filterId sharing the same attribute
  // key. Applying it fans the value out to every sibling path so it ORs across all
  // of them (the backend groups by orGroupKey). No-op when there are no siblings.
  //
  // Fan-out stays live for as long as the user is working inside the Add Filter
  // picker's options panel (meta.fromPicker), because checking five boxes on one
  // field reads as ONE act of filtering — it must not silently stop applying to
  // the other directions after the first checkbox. It also fires on the first
  // application from any source, so a single click still populates every direction.
  //
  // Independent per-direction editing begins when you edit an applied block
  // directly (no meta → no fan-out). A direction that has been edited that way has
  // diverged from its siblings, and is then left alone even by later picker edits —
  // otherwise re-opening the picker would silently overwrite a deliberate edit.
  const siblingIds = showFilterModuleV3 ? getSiblingFilterIds(flattenFilterConfig(filterConfig), id) : [];
  const attributeAlreadyApplied =
    prevValues.length > 0 ||
    siblingIds.some((sid) => (currentFilters[sid]?.length ?? 0) > 0);

  // Siblings still holding exactly what this filter held before this change — i.e.
  // never independently edited away from it. On a first application every sibling
  // is empty and so is prevValues, so this is all of them.
  const inSyncSiblingIds = siblingIds.filter((sid) =>
    sameValueSet(currentFilters[sid] ?? [], prevValues)
  );
  const fanOutTo =
    meta?.fromPicker || !attributeAlreadyApplied ? inSyncSiblingIds : [];

  // Handle removal — clears this filter, plus any in-sync siblings when fanning
  // out (clearing a field in the picker should not leave its other directions
  // behind, which — being OR'd — would keep matching on their own).
  if (value.length === 0) {
    const { [id]: _removed, ...remaining } = currentFilters;
    for (const sid of fanOutTo) delete remaining[sid];
    setActiveFilters(toOutput(remaining));
    return;
  }

  // Handle addition/update.
  const updated = { ...currentFilters, [id]: value };
  for (const sid of fanOutTo) updated[sid] = value;
  // Track new additions for analytics
  if (usedIn) {
    const gaCategory = getGACategory({ filterConfig, filterId: id });
    const newValue = value.find(
      (v) => !prevValues.some((pv) => pv.value === v.value)
    );
    if (newValue) {
      trackEventGA(gaCategory, `${usedIn} Filter`, newValue.display);
    }
  }
  setActiveFilters(toOutput(updated));
};
